import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShieldIcon from '@mui/icons-material/Shield';
import StorageIcon from '@mui/icons-material/Storage';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SearchIcon from '@mui/icons-material/Search';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import HistoryIcon from '@mui/icons-material/History';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import ComputerIcon from '@mui/icons-material/Computer';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SectionWrapper from '../components/shared/SectionWrapper';
import GradientText from '../components/shared/GradientText';
import AnimatedCard from '../components/shared/AnimatedCard';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const howItWorks = [
  {
    step: '01',
    title: 'Choose Your AI Agent',
    description:
      'Pick from pre-built agents or create your own. The General Data Agent is optimized for database analytics. The General Assistant handles research, writing, and web search. Each agent has its own tools, personality, and capabilities you can customize.',
    benefit:
      'No more context-switching between tools. One agent handles your entire workflow from question to insight.',
    icon: PsychologyIcon,
  },
  {
    step: '02',
    title: 'Connect Your Databases',
    description:
      'Add your data sources once — PostgreSQL, MySQL, SQL Server, Snowflake, Databricks, BigQuery, or SQLite. ToStructure stores connections securely and reuses them across conversations. Point it at a schema and it understands your tables automatically.',
    benefit:
      'Connect once, query forever. No JDBC strings to remember, no SQL clients to juggle.',
    icon: StorageIcon,
  },
  {
    step: '03',
    title: 'Teach It Your Business',
    description:
      'Save business questions and sample answers per data source. ToStructure injects them into context alongside the schema and sample rows, so the agent already knows the kinds of questions your team asks — and the right way to answer them.',
    benefit:
      'Onboarding new analysts takes minutes, not weeks. The agent inherits your team\'s institutional knowledge instead of guessing.',
    icon: QuestionAnswerIcon,
  },
  {
    step: '04',
    title: 'Drop Files Straight Into Chat',
    description:
      'Drag and drop a CSV, Excel, or Parquet file directly into the conversation. ToStructure imports it instantly and the AI can query, analyze, and visualize it on the spot — no setup, no config, no database needed.',
    benefit:
      'Got a spreadsheet from a colleague? Drop it in and ask questions immediately. Zero friction from file to insight.',
    icon: UploadFileIcon,
  },
  {
    step: '05',
    title: 'Ask Questions in Plain English',
    description:
      'Type what you want to know: "What were our top 10 products by revenue last quarter?" ToStructure writes the SQL, executes it against your database, and returns the results. If a query fails, it automatically retries with corrections.',
    benefit:
      'Analysts get answers in seconds instead of hours. Non-technical stakeholders can self-serve without waiting on engineering.',
    icon: SearchIcon,
  },
  {
    step: '06',
    title: 'Get Professional Visualizations',
    description:
      'ToStructure generates Highcharts-powered visualizations from your data — bar, column, line, area, pie, scatter, and spline charts with full control over colors, labels, stacking, series splitting, and metric formatting ($, %). Preview chart variations side-by-side, swap chart types on the fly, and restyle existing charts without re-running the SQL.',
    benefit:
      'Board-ready charts in seconds. Iterate on look-and-feel instantly without re-querying your warehouse, then export to PNG and drop directly into presentations.',
    icon: BarChartIcon,
  },
  {
    step: '07',
    title: 'Cite Everything',
    description:
      'Every answer includes full provenance. ToStructure tracks which database, table, and columns were queried, what SQL was executed, and which web sources were consulted. Generate APA, MLA, or Chicago citations with one click.',
    benefit:
      'Compliance teams love it. Every insight has an audit trail back to the source data.',
    icon: FormatQuoteIcon,
  },
  {
    step: '08',
    title: 'Watch the AI Think',
    description:
      'Real-time streaming shows every step as it happens: the SQL being generated, the query executing, search results loading, charts rendering. Full thinking logs let you understand exactly how the AI arrived at each answer.',
    benefit:
      'Complete transparency builds trust. You never have to wonder where an answer came from.',
    icon: VisibilityIcon,
  },
  {
    step: '09',
    title: 'Inspect Every Decision',
    description:
      'Open the context history viewer to see exactly what the AI saw — schema, sample rows, business questions, table relationships, and prior turns. Step through the inputs that produced each answer.',
    benefit:
      'When an answer surprises you, you can trace it back to the exact context that produced it. Perfect for debugging, audits, and building trust with stakeholders.',
    icon: HistoryIcon,
  },
];

const privacyPoints = [
  {
    icon: ComputerIcon,
    title: 'Run 100% Local',
    description:
      'Connect LM Studio or Ollama and run open-source models entirely on your machine. Your prompts, your data, your queries — nothing leaves your computer. Ever.',
  },
  {
    icon: CloudOffIcon,
    title: 'No Cloud Required',
    description:
      'ToStructure works offline. The FastAPI backend runs locally on your machine, your databases can be local or remote, and local models need zero internet. The only time data leaves your computer is when you explicitly choose a cloud AI provider.',
  },
  {
    icon: LockIcon,
    title: 'Your Data Stays Yours',
    description:
      'Database credentials are stored locally. Conversation history lives on your disk. There is no ZerveMe server collecting your queries or training on your data. When you delete a conversation, it is gone.',
  },
  {
    icon: ShieldIcon,
    title: 'Enterprise-Grade Architecture',
    description:
      'Built with a 4-layer backend architecture (controllers, managers, services, repositories) and 248+ automated tests. The same engineering rigor that enterprises demand for their production systems.',
  },
];

const databases = [
  { name: 'PostgreSQL', desc: 'The world\'s most advanced open-source relational database' },
  { name: 'MySQL', desc: 'The most popular open-source database for web applications' },
  { name: 'SQL Server', desc: 'Microsoft\'s enterprise relational database' },
  { name: 'Snowflake', desc: 'Cloud-native data warehouse for analytics at scale' },
  { name: 'Databricks', desc: 'Unified analytics platform built on Apache Spark' },
  { name: 'BigQuery', desc: 'Google\'s serverless, highly scalable data warehouse' },
  { name: 'SQLite', desc: 'Lightweight embedded database — perfect for local data and CSV imports' },
];

const aiProviders = [
  {
    name: 'Anthropic Claude',
    desc: 'Advanced reasoning and analysis with Claude 3.5 and Claude 4 models',
    local: false,
  },
  {
    name: 'OpenAI GPT',
    desc: 'GPT-4o and GPT-4 Turbo for versatile language understanding',
    local: false,
  },
  {
    name: 'DeepSeek',
    desc: 'High-performance models optimized for code and data tasks',
    local: false,
  },
  {
    name: 'LM Studio',
    desc: 'Run any GGUF model locally — Llama, Mistral, Phi, and more. Zero cloud dependency.',
    local: true,
  },
  {
    name: 'Ollama',
    desc: 'Pull and run open-source models with one command. Fully local, fully private.',
    local: true,
  },
];

const whoItsFor = [
  {
    title: 'Data Analysts',
    points: [
      'Query any database without writing SQL',
      'Generate presentation-ready charts in seconds',
      'Compare metrics across multiple data sources',
      'Export findings with full citations for stakeholders',
    ],
  },
  {
    title: 'Business Leaders',
    points: [
      'Get answers from company data without waiting on engineering',
      'Self-serve analytics in plain English',
      'Make data-driven decisions faster',
      'Trust every insight with transparent source tracking',
    ],
  },
  {
    title: 'Researchers',
    points: [
      'Combine database queries with web research',
      'Auto-generate APA, MLA, or Chicago citations',
      'Track every source across data and web',
      'Export conversations as PDF or markdown',
    ],
  },
  {
    title: 'Engineers & Data Scientists',
    points: [
      'Run local models for complete data privacy',
      'Create custom agents with specialized tools',
      'Switch between 5 AI providers mid-session',
      'Self-host with the FastAPI backend',
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Animations                                                         */
/* ------------------------------------------------------------------ */

const fade = {
  initial: { opacity: 0, y: 20 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true, margin: '-40px' as const },
  transition: { duration: 0.6, ease: 'easeOut' as const },
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const ToStructurePage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <Box component="main" sx={{ flex: 1 }}>
        {/* ===== HERO ===== */}
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#0c0c0c',
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: 'center', py: 16 }}>
            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}>
              <Chip
                label="NATIVE DESKTOP APP — MAC & WINDOWS"
                size="small"
                sx={{
                  mb: 4,
                  fontWeight: 600,
                  letterSpacing: 2,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(255, 255, 255, 0.04)',
                  color: 'text.secondary',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              />
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '4rem', md: '5.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.0,
                  mb: 3,
                  color: '#ffffff',
                }}
              >
                ToStructure
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.35 }}>
              <Typography
                variant="h4"
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  mb: 3,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: 'primary.main',
                  fontFamily: '"Bricolage Grotesque", sans-serif',
                }}
              >
                Your data. Your models. Your machine.
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.5 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 520, mx: 'auto', mb: 6, lineHeight: 1.8 }}
              >
                An AI-powered data analytics app for Mac and Windows that connects to your databases,
                generates visualizations, and delivers citation-ready insights through
                natural conversation. Run it entirely local — your data never leaves your machine.
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.65 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    const el = document.querySelector('#how-it-works');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  See How It Works
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ShieldIcon />}
                  onClick={() => {
                    const el = document.querySelector('#privacy');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'transparent' },
                  }}
                >
                  Privacy First
                </Button>
              </Stack>
            </motion.div>
          </Container>
        </Box>

        {/* ===== PRIVACY & LOCAL-FIRST ===== */}
        <SectionWrapper id="privacy" dark>
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                LOCAL-FIRST ARCHITECTURE
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 520 }}>
                Your data never leaves your machine
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540 }}>
                ToStructure is built from the ground up for privacy. Run open-source LLMs
                through LM Studio or Ollama and keep every prompt, query, and result
                completely local. No telemetry. No cloud dependency. No compromise.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {privacyPoints.map((point, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={point.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <point.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {point.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {point.description}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== HOW IT WORKS ===== */}
        <SectionWrapper id="how-it-works">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3 }}>
                How it works
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
                From question to insight in nine steps. Every step is visible,
                every answer is traceable.
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {howItWorks.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: 0.05 }}
              >
                <Card sx={{ p: { xs: 3, md: 5 } }}>
                  <Grid container spacing={4} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Bricolage Grotesque", sans-serif',
                          fontSize: '2rem',
                          fontWeight: 800,
                          color: 'rgba(255, 255, 255, 0.08)',
                          lineHeight: 1,
                        }}
                      >
                        {step.step}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            flexShrink: 0,
                          }}
                        >
                          <step.icon sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {step.description}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          height: '100%',
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            letterSpacing: 2,
                            fontSize: '0.65rem',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          WHY IT MATTERS
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {step.benefit}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </motion.div>
            ))}
          </Box>
        </SectionWrapper>

        {/* ===== DATABASE SUPPORT ===== */}
        <SectionWrapper id="databases" dark>
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3 }}>
                Every database you need
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
                Connect once, query forever. ToStructure understands your schema
                and generates optimized SQL for each database engine.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2}>
            {databases.map((db, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={db.name}>
                <AnimatedCard delay={i * 0.05}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <StorageIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      {db.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {db.desc}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== PROJECTS & MULTI-TABLE ===== */}
        <SectionWrapper id="projects-multi-table">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                PROJECTS &amp; MULTI-TABLE ANALYSIS
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 560 }}>
                Organize your data, model your relationships
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                Real analytics rarely lives in a single table. Group your connections,
                files, and conversations into projects, then teach the agent how your
                tables relate — so it can write JOINs that actually run.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {[
              {
                icon: WorkspacesIcon,
                title: 'Projects as workspaces',
                description:
                  'Group connections, documents, and chat sessions into projects. Each one keeps its own context, so analyses don\'t bleed between domains, clients, or teams.',
              },
              {
                icon: AccountTreeIcon,
                title: 'Table relationships',
                description:
                  'Define foreign keys and joins once per data source. The agent reuses them across every conversation — no re-explaining schemas, no manual stitching.',
              },
              {
                icon: HubIcon,
                title: 'Multi-table queries',
                description:
                  'Ask questions that span tables. The agent plans the JOINs, executes the combined query, and returns a single coherent answer with citations across every source it touched.',
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <item.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item.description}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== AI PROVIDERS ===== */}
        <SectionWrapper id="ai-providers">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3 }}>
                Choose your AI
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
                Use the best cloud models when you need maximum intelligence, or
                switch to local models when privacy is non-negotiable. Mix and match
                mid-conversation.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {aiProviders.map((provider, i) => (
              <Grid
                size={{ xs: 12, sm: 6, md: provider.local ? 6 : 4 }}
                key={provider.name}
              >
                <AnimatedCard delay={i * 0.06} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  {provider.local && (
                    <Chip
                      label="LOCAL"
                      size="small"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        letterSpacing: 1,
                        fontSize: '0.65rem',
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        color: 'text.secondary',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {provider.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {provider.desc}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>

          <motion.div {...fade}>
            <Box
              sx={{
                mt: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                bgcolor: '#141414',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#ffffff' }}>
                The local model advantage
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.8 }}>
                When you run LM Studio or Ollama, your data pipeline is fully local:
                your database query goes from ToStructure to your database and back,
                the AI processes it on your own hardware, and the chart renders on your screen.
                At no point does your data touch an external server. This is not
                a toggle or a "privacy mode" — it is the architecture.
              </Typography>
            </Box>
          </motion.div>
        </SectionWrapper>

        {/* ===== WHO IT'S FOR ===== */}
        <SectionWrapper id="who-its-for" dark>
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 520 }}>
                Built for everyone who touches data
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
                Whether you write SQL daily or have never opened a terminal,
                ToStructure meets you where you are.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {whoItsFor.map((persona, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={persona.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    {persona.title}
                  </Typography>
                  <List disablePadding dense>
                    {persona.points.map((point) => (
                      <ListItem key={point} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={point}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            sx: { lineHeight: 1.6 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== BUILT TO LAST ===== */}
        <SectionWrapper id="built-to-last">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3 }}>
                Built to last
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                A real desktop app — not a browser tab. Fast, reliable, and
                thoroughly tested so you can focus on your data, not on your tools.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {[
              {
                icon: ComputerIcon,
                title: 'Native desktop app',
                desc: 'Runs directly on your Mac or Windows PC with full native performance. No browser required.',
              },
              {
                icon: ShieldIcon,
                title: 'Rigorously tested',
                desc: '248+ automated tests ensure everything works reliably, from database queries to chart generation.',
              },
              {
                icon: UploadFileIcon,
                title: 'Export anything',
                desc: 'Save charts as PNG, export conversations as PDF or markdown, and share findings with your team.',
              },
              {
                icon: VisibilityIcon,
                title: 'Real-time responses',
                desc: 'Watch results stream in live as the AI works — no waiting for a spinner, you see progress instantly.',
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={item.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <item.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item.desc}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== CTA ===== */}
        <SectionWrapper
          id="product-contact"
          sx={{ py: { xs: 12, md: 20 }, bgcolor: '#0c0c0c' }}
        >
          <motion.div {...fade}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{ fontWeight: 700, mb: 3, color: '#ffffff', fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Ready to talk to your data?
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 460, mx: 'auto', mb: 6, lineHeight: 1.8 }}
              >
                Get in touch to learn more about ToStructure, request early access,
                or see a live demo with your own data.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<EmailIcon />}
                  href="mailto:kevin@zerveme.com"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  Request Early Access
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LinkedInIcon />}
                  href="https://www.linkedin.com/in/kevin-singh-908547b4"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'transparent' },
                  }}
                >
                  Connect on LinkedIn
                </Button>
              </Stack>

              <Button
                component={RouterLink}
                to="/"
                sx={{ mt: 5, color: 'text.secondary', fontSize: '0.85rem', '&:hover': { color: '#ffffff' } }}
              >
                Back to ZerveMe.com
              </Button>
            </Box>
          </motion.div>
        </SectionWrapper>
      </Box>

      <Footer />
    </Box>
  );
};

export default ToStructurePage;
